import { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ArrowLeft, MapPinned, Eye, Route, Flame, PawPrint } from "lucide-react";
import { api, photoUrl } from "../lib/api";
import { geocodeListing, PROV_CENTER } from "../lib/constants";
import { useLanguage } from "../context/LanguageContext";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { MissingDuration } from "../components/MissingDuration";
import { HeatLayer } from "../components/HeatLayer";
import { HomePopup } from "../components/HomePopup";
import { LostFoundId } from "../components/LostFoundId";

const COLORS = { lost: "#DC2626", found: "#059669", reunited: "#14B8A6" };
const typeOf = (l) => (l.reunited_at ? "reunited" : l.lf_type);
const pin = (color, active) => L.divIcon({
  className: "",
  html: `<svg width="${active ? 36 : 28}" height="${active ? 48 : 38}" viewBox="0 0 30 40"><path d="M15 0C6.7 0 0 6.7 0 15c0 10.5 15 25 15 25s15-14.5 15-25C30 6.7 23.3 0 15 0z" fill="${color}" stroke="#fff" stroke-width="2"/><circle cx="15" cy="15" r="5.5" fill="#fff"/></svg>`,
  iconSize: active ? [36, 48] : [28, 38], iconAnchor: active ? [18, 48] : [14, 38], popupAnchor: [0, -36],
});
const TYPES = ["all", "lost", "found", "reunited"];

function FitAll({ points }) {
  const map = useMap();
  const key = points.map((p) => p.join(",")).join("|");
  useEffect(() => {
    if (points.length > 1) map.fitBounds(L.latLngBounds(points).pad(0.2), { maxZoom: 12 });
    else if (points.length === 1) map.setView(points[0], 11);
    else map.setView(PROV_CENTER.all, 4);
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

const Trail = ({ l, color, t }) => {
  const pts = l.sightings.map((s) => [s.lat, s.lng]);
  if (!pts.length) return null;
  return (
    <>
      <Polyline positions={pts} pathOptions={{ color, weight: 3, dashArray: "6 6", opacity: 0.8 }} />
      {l.sightings.map((s, i) => (
        <CircleMarker key={s.id} center={[s.lat, s.lng]} radius={i === pts.length - 1 ? 8 : 5}
          pathOptions={{ color: "#fff", weight: 2, fillColor: color, fillOpacity: 1 }}>
          <Popup><p className="text-xs font-semibold">{t("lmap_sighting")} {i + 1}</p><p className="text-[11px] text-slate-500">{new Date(s.seen_at).toLocaleString()}</p>{s.note && <p className="text-[11px] mt-1">{s.note}</p>}</Popup>
        </CircleMarker>
      ))}
    </>
  );
};

const ReportRow = ({ l, active, onPick, t }) => (
  <button onClick={() => onPick(l.id)} data-testid={`lfm-row-${l.id}`}
    className={`w-full text-left flex gap-3 p-2.5 rounded-xl border transition-colors ${active ? "border-[#F5551E] bg-[#FFF4F0]" : "border-transparent hover:bg-slate-50"}`}>
    {(l.photos || [])[0]
      ? <img src={photoUrl(l.photos[0])} alt="" className="h-14 w-14 rounded-lg object-cover bg-slate-100 shrink-0" />
      : <span className="h-14 w-14 rounded-lg bg-slate-100 shrink-0 flex items-center justify-center"><PawPrint size={20} className="text-slate-300" /></span>}
    <div className="min-w-0">
      <span className="text-[10px] font-bold uppercase tracking-wide text-white px-1.5 py-0.5 rounded" style={{ background: COLORS[typeOf(l)] }}>{t(`lmap_${typeOf(l)}`)}</span>
      {l.lf_id && <span className="ml-1"><LostFoundId listing={{ ...l, category: "lost-found" }} testid={`lfm-list-id-${l.id}`} /></span>}
      <p className="text-sm font-semibold text-[#12234A] truncate mt-1">{l.title}</p>
      <p className="text-[11px] text-slate-500 truncate">{l.city}, {l.province}{l.sightings.length ? ` · ${l.sightings.length} ${t("lmap_sightings")}` : ""}</p>
    </div>
  </button>
);

export default function LostFoundMap() {
  const { t } = useLanguage();
  const [type, setType] = useState("all");
  const [days, setDays] = useState("30");
  const [items, setItems] = useState(null);
  const [picked, setPicked] = useState(null);
  const [allTrails, setAllTrails] = useState(false);
  const [heat, setHeat] = useState(false);

  useEffect(() => {
    setItems(null);
    api.get(`/lost-found/map?type=${type}&days=${days}`).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, [type, days]);
  const marked = useMemo(() => (items || []).map((l) => {
    const last = l.sightings[l.sightings.length - 1];
    return { ...l, pos: last ? [last.lat, last.lng] : geocodeListing(l) };
  }), [items]);
  const points = marked.map((m) => m.pos);
  const sightCount = marked.reduce((n, l) => n + l.sightings.length, 0);
  const heatPoints = useMemo(() => marked.flatMap((l) => l.sightings.map((s) => [s.lat, s.lng, 1])), [marked]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="lost-found-map-page">
      <HomePopup page="lostfound" />
      <Link to="/browse?category=lost-found" data-testid="lfm-back" className="text-sm text-slate-500 hover:text-[#F5551E] inline-flex items-center gap-1"><ArrowLeft size={15} /> {t("lmap_back")}</Link>
      <div className="mt-3 flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#12234A] flex items-center gap-2"><MapPinned className="text-red-600" size={30} /> {t("lmap_title")}</h1>
          <p className="text-slate-500 mt-1 text-sm sm:text-base max-w-2xl">{t("lmap_sub")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex bg-white rounded-xl border border-slate-200 p-1 gap-1" data-testid="lfm-type-tabs">
            {TYPES.map((x) => (
              <button key={x} onClick={() => { setType(x); setPicked(null); }} data-testid={`lfm-type-${x}`}
                className={`px-3 h-8 rounded-lg text-sm font-semibold transition-colors ${type === x ? "text-white" : "text-slate-600 hover:bg-slate-50"}`}
                style={type === x ? { background: COLORS[x] || "#12234A" } : undefined}>{t(`lmap_${x}`)}</button>
            ))}
          </div>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="h-10 w-40 rounded-xl bg-white" data-testid="lfm-days"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              {["7", "30", "90", "365"].map((d) => <SelectItem key={d} value={d} data-testid={`lfm-days-${d}`}>{t(`lmap_d${d}`)}</SelectItem>)}
            </SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer"><Switch checked={allTrails} onCheckedChange={setAllTrails} data-testid="lfm-trails-toggle" /> <Route size={15} /> {t("lmap_trails")}</label>
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer" title={t("lmap_heat_hint")}><Switch checked={heat} onCheckedChange={setHeat} data-testid="lfm-heat-toggle" /> <Flame size={15} className="text-orange-500" /> {t("lmap_heat")}</label>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-5 mt-6">
        <aside className="lg:col-span-4 order-2 lg:order-1">
          <div className="bg-white rounded-2xl border border-slate-200/80 p-3 lg:h-[640px] lg:overflow-y-auto">
            <p className="text-xs text-slate-500 px-1 pb-2" data-testid="lfm-count"><strong className="text-[#12234A]">{marked.length}</strong> {t(marked.length === 1 ? "lmap_report" : "lmap_reports")} · <Eye size={12} className="inline" /> {sightCount} {t("lmap_sightings")}</p>
            {items !== null && !marked.length && <p className="text-sm text-slate-400 p-4 text-center" data-testid="lfm-empty">{t("lmap_none")}</p>}
            {marked.length > 0 && !picked && !allTrails && <p className="text-[11px] text-slate-400 px-1 pb-2">{t("lmap_trail_hint")}</p>}
            <div className="space-y-1">{marked.map((l) => <ReportRow key={l.id} l={l} active={picked === l.id} onPick={setPicked} t={t} />)}</div>
          </div>
        </aside>
        <div className="lg:col-span-8 order-1 lg:order-2 rounded-2xl overflow-hidden border border-slate-200 h-[460px] lg:h-[640px]" data-testid="lfm-map">
          <MapContainer center={PROV_CENTER.all} zoom={4} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
            <TileLayer attribution="&copy; OpenStreetMap contributors" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <FitAll points={points} />
            {heat && <HeatLayer points={heatPoints} />}
            {marked.map((l) => {
              const color = COLORS[typeOf(l)];
              return (
                <Fragment key={l.id}>
                  {(allTrails || picked === l.id) && <Trail l={l} color={color} t={t} />}
                  <Marker position={l.pos} icon={pin(color, picked === l.id)} eventHandlers={{ click: () => setPicked(l.id) }}>
                    <Popup>
                      <div className="w-48" data-testid={`lfm-popup-${l.id}`}>
                        <img src={photoUrl((l.photos || [])[0])} alt="" className="h-24 w-full rounded-lg object-cover bg-slate-100" />
                        <p className="font-semibold text-[#12234A] text-sm mt-1.5 leading-tight">{l.title}</p>
                        {l.lf_id && <div className="mt-1"><LostFoundId listing={{ ...l, category: "lost-found" }} testid={`lfm-popup-id-${l.id}`} /></div>}
                        <p className="text-[11px] text-slate-500">{l.city}, {l.province}</p>
                        {typeOf(l) === "lost" && l.lf_date && <div className="mt-1"><MissingDuration listing={l} /></div>}
                        <Link to={`/listing/${l.id}`} data-testid={`lfm-open-${l.id}`} className="mt-1.5 inline-block text-xs font-semibold text-[#F5551E] hover:underline">{t("lmap_open")} →</Link>
                      </div>
                    </Popup>
                  </Marker>
                </Fragment>
              );
            })}
          </MapContainer>
        </div>
      </div>
    </div>
  );
}
